import { createAction } from '@reduxjs/toolkit';
import { IReportFilter, ReportType } from 'core/slices/report';
import { IUploadFile } from 'core/slices/upload';
import { UploadTypes } from 'types/app/upload';
import { SortBy } from 'types/domain/search';
import { EntityTypes } from '../../../constants/entities';
import { IText } from 'types/domain/text';
import { Handles } from 'constants/handles';
import { IAction, IActionDAO, IActionEntity, IActionEntityBasic } from 'types/domain/action';

export interface IGet {
  entityId?: number;
  type: EntityTypes;
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  report?: boolean;
}

export interface IList {
  type: EntityTypes;
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  search?: string;
  filter?: any;
  sort?: number;
  pagerInfo: IPage;
  componentId?: string;
}

export interface IReportList extends IList {
  reportType?: ReportType;
  filter?: IReportFilter;
}

export interface IPage {
  page: number;
  pageCount?: number;
  totalRecords?: number;
  search?: string;
  sort?: SortBy;
  ids?: number[];
  start?: Date;
  end?: Date;
  days?: number;
  timeZone?: string;
}

export interface ICreate {
  type: EntityTypes;
  entity: any;
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  parentEntityAttribute?: string;
  parentModuleId?: number;
  parentCourseId?: number;
  parentOrganisationId?: number;
}

export interface IClone extends ICreate {
  token?: string;
}

export interface IUpdate {
  type: EntityTypes;
  entity: any;
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  parentEntityAttribute?: string;
  parentModuleId?: number;
  parentCourseId?: number;
  persist?: boolean;
  mergeData?: boolean;
}

export interface IEmailRequest {
  courseProgressIds: number[];
  courseNodeId: number;
  subject: string;
  body: IText;
}

export interface IBatch {
  batch: IUpdate[];
  moduleId?: number;
  courseId?: number;
}

export interface IUpload {
  files: IUploadFile[];
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  multi?: boolean;
  type: UploadTypes;
}

export interface IUploadResponse {
  url: string;
  filename: string;
}

export interface IActionCreate extends Partial<IActionDAO> {
  handle?: Handles | number;
  parentActionId?: number;
  parentEntityId?: number;
  modOutId?: number;
  targetId?: number;
  edit?: string;
  addLast?: boolean;
  /** `createPreviousAction` only: insert ahead of this action rather than the selected one. */
  beforeActionId?: number;
  entities?: IActionEntityBasic[];
}

export const createNextAction = createAction('admin/create/nextAction', function prepare(entity?: IActionCreate) {
  return {
    payload: {
      ...(entity || {}),
    },
  };
});

export const createPreviousAction = createAction('admin/create/previousAction', function prepare(entity?: any) {
  return {
    payload: {
      ...(entity || {}),
    },
  };
});

export const update = createAction('admin/update', function prepare(entity: IUpdate) {
  return {
    payload: {
      ...entity,
    },
  };
});

/**
 * Sends every debounced `update` now rather than after its delay. Answered by
 * `savesFlushed` with the same id once the requests have settled.
 */
export const flushSaves = createAction('admin/flushSaves', function prepare(id: string) {
  return { payload: { id } };
});

export const savesFlushed = createAction('admin/savesFlushed', function prepare(id: string) {
  return { payload: { id } };
});

export const batch = createAction('admin/batch', function prepare(data: IBatch) {
  return { payload: data };
});

/**
 * A change someone else made (another author, another tab, the MCP, a learner's progress),
 * pushed over the websocket. Merged like a save response, never persisted, and kept out
 * of undo history - undoing must not revert another person's edit.
 *
 * `mergeEntity: false` applies only the side effects (a text's translation flags) without
 * overwriting the entity - e.g. the base text changed while this tab edits a translation.
 */
export interface IRemoteUpdate {
  type: EntityTypes;
  entity: any;
  parentModuleId?: number;
  mergeEntity: boolean;
}

export const remoteUpdate = createAction('admin/remote', function prepare(data: IRemoteUpdate) {
  return { payload: data };
});

/**
 * Rows fetched outside the store, merged in locally and never saved.
 *
 * A summary merge: a null in the row never overwrites a value already held, so
 * a listed row cannot blank out a fuller copy another load brought in.
 */
export const seed = createAction('admin/seed', function prepare(type: EntityTypes, list: unknown[]) {
  return { payload: { type, list } };
});
